import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function About() {
  useScrollAnimation();

  useEffect(() => {
    document.title = 'About Us | Audacious Quest PTE Ltd';
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[420px] md:h-[500px] overflow-hidden scroll-animate">
        <img
          src="https://readdy.ai/api/search-image?query=Pilot%20in%20airplane%20cockpit%20looking%20out%20at%20dramatic%20orange%20and%20gold%20sunset%20sky%20through%20aircraft%20window%2C%20aviation%20journey%20aspiration%20and%20vision%20concept%2C%20warm%20amber%20golden%20tones%2C%20cinematic%20wide%20angle%20photography%2C%20epic%20inspirational%20atmosphere&width=1600&height=500&seq=about-hero&orientation=landscape"
          alt="An Alternate Viewpoint"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white text-center drop-shadow-lg px-4">
            An Alternate Viewpoint
          </h2>
        </div>
      </section>

      {/* About Us Text */}
      <section className="py-14 md:py-20 px-4 md:px-6 scroll-animate">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-foreground-500 mb-2 tracking-wide">About Us</p>
          <p className="text-lg md:text-xl text-foreground-800 leading-relaxed mb-6">
            The need for technological answers to the world&apos;s problems should continue to stimulate
            demand for technology. There are always lucrative investment possibilities that coincide
            with the acceleration of digital innovation and adoption. The tech market has risen from
            US$2 trillion to US$13 trillion in size today.
          </p>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            Audacious Quest has an alternative viewpoint when it comes to investments. We take
            calculated risks seriously, so choosing and managing our investments is crucial. We are
            aware of how critical it is to react promptly to market developments in order to safeguard
            and increase your investment. Our goal is to establish ourselves as a dependable partner
            that truly gets you.
          </p>
        </div>
      </section>

      {/* Handshake + Quote */}
      <section className="relative scroll-animate">
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Close%20up%20of%20two%20businessmen%20in%20formal%20dark%20suits%20shaking%20hands%20firmly%20in%20agreement%2C%20warm%20golden%20side%20lighting%20from%20window%2C%20business%20partnership%20and%20trust%20concept%2C%20shallow%20depth%20of%20field%2C%20premium%20corporate%20photography%20with%20elegant%20bokeh%20background&width=1600&height=400&seq=about-handshake&orientation=landscape"
            alt="Partnership"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 flex items-center justify-center px-6 md:px-12">
            <p className="text-lg md:text-2xl lg:text-3xl text-white text-center max-w-3xl leading-relaxed font-heading drop-shadow-lg">
              There are always lucrative possibilities that coincide with the acceleration of digital
              innovation and adoption
            </p>
          </div>
        </div>
      </section>

      {/* Fund / Investment Portfolio */}
      <section className="py-14 md:py-20 px-4 md:px-6 scroll-animate">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading text-primary-900 mb-8">
            Strategic Nucleus
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="group">
              <div className="overflow-hidden mb-3 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Silhouette%20of%20business%20executive%20looking%20out%20private%20jet%20window%20at%20golden%20sunrise%20over%20rolling%20hills%2C%20cinematic%20corporate%20travel%20perspective%2C%20warm%20amber%20and%20deep%20blue%20tones%2C%20soft%20lens%20flare%2C%20premium%20editorial%20aviation%20photography%20with%20moody%20atmospheric%20lighting&width=500&height=320&seq=about-consultancy-v3&orientation=landscape"
                  alt="Consultancy"
                  className="w-full h-52 md:h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900">Consultancy</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mt-2">
                Leveraging extensive market knowledge and industry insights, we confidently steer
                through complex markets to secure sustainable results.
              </p>
            </div>
            <div className="group">
              <div className="overflow-hidden mb-3 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Close%20up%20of%20businessman%20in%20charcoal%20suit%20holding%20modern%20tablet%20with%20glowing%20white%20holographic%20financial%20data%20visualization%20floating%20above%20screen%2C%20clean%20minimal%20gray%20background%2C%20strategic%20investment%20analysis%20concept%2C%20premium%20corporate%20product%20photography%20with%20soft%20even%20lighting&width=500&height=320&seq=about-investment-v3&orientation=landscape"
                  alt="Investment"
                  className="w-full h-52 md:h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900">Investment</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mt-2">
                Dedicated to fostering long-term value through meticulous statistical analysis.
              </p>
            </div>
            <div className="group">
              <div className="overflow-hidden mb-3 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20diverse%20business%20team%20of%20four%20executives%20in%20formal%20attire%20gathered%20around%20a%20sleek%20dark%20wood%20conference%20table%20reviewing%20documents%20and%20laptop%2C%20strategic%20trading%20discussion%2C%20moody%20warm%20ambient%20office%20lighting%20with%20city%20night%20views%20through%20window%2C%20high-end%20corporate%20editorial%20photography&width=500&height=320&seq=about-trading-v3&orientation=landscape"
                  alt="Trading"
                  className="w-full h-52 md:h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900">Trading</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mt-2">
                Utilizing in-depth market understanding to pinpoint strategic oppotunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}